"use client";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import JSZip from "jszip";
import TagChips, {
  matchesTextQuery,
  buildTagOptions,
  hasAllTags,
} from "./Pieceworktags";

const API = "https://erp-xe9u.onrender.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const cls = (...c) => c.filter(Boolean).join(" ");

const STATUS_STYLES = {
  pending: { badge: "bg-yellow-700/60 text-yellow-100", dot: "bg-yellow-400" },
  approved: { badge: "bg-green-700/60 text-green-100", dot: "bg-green-400" },
  rejected: { badge: "bg-red-800/60 text-red-200", dot: "bg-red-400" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || {
    badge: "bg-gray-700 text-gray-400",
    dot: "bg-gray-500",
  };
  return (
    <span
      className={cls(
        "inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-semibold rounded-full",
        s.badge,
      )}
    >
      <span className={cls("w-1.5 h-1.5 rounded-full", s.dot)} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// ── Cost helpers ──────────────────────────────────────────────────────────
const logSubtotal = (log) =>
  (log.lineItems || []).reduce((s, li) => s + (li.subtotal || 0), 0);

const logBurden = (log) =>
  (log.lineItems || []).reduce((s, li) => s + (li.burden || 0), 0);

// ── PDF Generation ────────────────────────────────────────────────────────
// Generates one worksheet per log, appending pages into a single PDF doc.
// ── PDF Generation ────────────────────────────────────────────────────────
// Worker-facing worksheet. Shows the rate they actually earned per unit
// (base pay ÷ qty) and their base pay subtotal. Burden is intentionally
// omitted — it's internal cost accounting, not worker pay.
// ── PDF Generation ────────────────────────────────────────────────────────
// Worker-facing worksheet. Shows the rate they actually earned per unit
// (base pay ÷ qty) and their base pay subtotal. Burden is intentionally
// omitted — it's internal cost accounting, not worker pay.
// ── PDF Generation ────────────────────────────────────────────────────────
// Worker-facing worksheet. Shows the rate they actually earned per unit
// (base pay ÷ qty) and their base pay subtotal. Burden is intentionally
// omitted — it's internal cost accounting, not worker pay.
// ── PDF Generation ────────────────────────────────────────────────────────
// Worker-facing worksheet. Shows the rate they actually earned per unit
// (base pay ÷ qty) and their base pay subtotal. Burden is intentionally
export const buildPDF = (logs, project) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "letter",
  });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const BRAND = [26, 90, 60];
  const ACCENT = [220, 100, 30];
  const LIGHT_GRAY = [245, 246, 247];
  const MID_GRAY = [180, 185, 190];
  const DARK = [30, 35, 40];
  const WHITE = [255, 255, 255];

  const projectName =
    project?.projectName || project?.projectNumber || "Project";
  const projectNum = project?.projectNumber || "";

  logs.forEach((log, logIdx) => {
    if (logIdx > 0) doc.addPage();

    const woTitle = log.workOrder?.title || "—";
    const woNum = log.workOrder?.woNumber || "";
    const worker = log.worker?.displayName || log.worker?.email || "—";
    const logNum = log.logNumber || "—";
    const status = (log.status || "").toUpperCase();
    const workDate = fmtDateOnly(log.workCompletedDate);
    const submittedDate = log.submittedAt
      ? new Date(log.submittedAt).toLocaleDateString("en-CA")
      : "—";
    // ── Header bar ──────────────────────────────────────────────────────
    doc.setFillColor(...BRAND);
    doc.rect(0, 0, pageW, 18, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...WHITE);
    doc.text("PIECEWORK WORKSHEET", 10, 7.5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`${projectName}${projectNum ? "  ·  " + projectNum : ""}`, 10, 13);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...WHITE);
    doc.text(logNum, pageW - 10, 8, { align: "right" });

    const statusColors = {
      APPROVED: [34, 197, 94],
      PENDING: [234, 179, 8],
      REJECTED: [239, 68, 68],
    };
    const pillColor = statusColors[status] || MID_GRAY;
    const pillW = 22;
    const pillX = pageW - 10 - pillW;
    doc.setFillColor(...pillColor);
    doc.roundedRect(pillX, 10, pillW, 6, 1.5, 1.5, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...WHITE);
    doc.text(status, pillX + pillW / 2, 14, { align: "center" });

    // ── Meta row ────────────────────────────────────────────────────────
    const metaY = 21;
    doc.setFillColor(...LIGHT_GRAY);
    doc.rect(0, 18, pageW, 14, "F");

    const metaFields = [
      ["Work Order", `${woTitle}${woNum ? "  (" + woNum + ")" : ""}`],
      ["Worker", worker],
      ["Work Date", workDate],
      ["Submitted", submittedDate],
    ];

    const colW = pageW / metaFields.length;
    metaFields.forEach(([label, value], i) => {
      const x = 10 + i * colW;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...MID_GRAY);
      doc.text(label.toUpperCase(), x, metaY);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(...DARK);
      doc.text(value, x, metaY + 5.5);
    });

    doc.setDrawColor(...MID_GRAY);
    doc.setLineWidth(0.2);
    doc.line(0, 32, pageW, 32);

    // ── Line items table ─────────────────────────────────────────────────
    const tableTop = 34;
    const lineItems = log.lineItems || [];

    const tableColumns = [
      { header: "Section", dataKey: "section" },
      { header: "Bid Area", dataKey: "bidArea" },
      { header: "Item Code", dataKey: "itemCode" },
      { header: "Description", dataKey: "description" },
      { header: "UOM", dataKey: "uom" },
      { header: "Qty", dataKey: "qty" },
      // "Pay Rate" = what they actually earned per unit (subtotal ÷ qty).
      // This is what the worker cares about — not the catalog rate or the
      // burden-inclusive rate. For most logs these will match; a manual
      // override or rounding may cause a small difference.
      { header: "Pay Rate", dataKey: "payRate" },
      // "Pay" = their base pay for this line (subtotal, no burden)
      { header: "Pay", dataKey: "subtotal" },
    ];

    // Pay = stored subtotal — the actual amount paid to the worker.
    // Pay Rate = subtotal / qty, shown to the fewest decimal places (2-4)
    // that round-trip cleanly: displayed qty × displayed rate = displayed pay.
    const computedLines = lineItems.map((li) => {
      const qty = parseFloat(li.quantity) || 0;
      const linePay = parseFloat(li.subtotal) || 0;

      let payRateStr = fmt(parseFloat(li.unitRate) || 0); // 2dp fallback

      if (qty > 0) {
        const exact = linePay / qty;
        let found = false;
        for (const dp of [2, 3, 4]) {
          const factor = Math.pow(10, dp);
          const rounded = Math.round(exact * factor) / factor;
          if (
            Math.round(rounded * qty * 100) / 100 ===
            Math.round(linePay * 100) / 100
          ) {
            payRateStr = rounded.toLocaleString("en-US", {
              minimumFractionDigits: dp,
              maximumFractionDigits: dp,
            });
            found = true;
            break;
          }
        }
        if (!found) {
          const r4 = Math.round(exact * 10000) / 10000;
          payRateStr = r4.toLocaleString("en-US", {
            minimumFractionDigits: 4,
            maximumFractionDigits: 4,
          });
        }
      }

      return { li, qty, payRateStr, linePay };
    });

    // Footer total = sum of stored subtotals (what was actually paid)
    const workerPayTotal = computedLines.reduce((s, r) => s + r.linePay, 0);

    const tableRows = computedLines.map(({ li, qty, payRateStr, linePay }) => ({
      section: li.sectionNumber || "—",
      bidArea: li.bidArea || "—",
      itemCode: li.itemCode || "—",
      description: li.description || "—",
      uom: li.uom || "—",
      qty: qty.toLocaleString("en-US", { maximumFractionDigits: 4 }),
      payRate: `$${payRateStr}`,
      subtotal: `$${fmt(linePay)}`,
    }));

    autoTable(doc, {
      startY: tableTop,
      columns: tableColumns,
      body: tableRows,
      theme: "plain",
      styles: {
        fontSize: 7.5,
        cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
        textColor: DARK,
        font: "helvetica",
        overflow: "ellipsize",
      },
      headStyles: {
        fillColor: DARK,
        textColor: WHITE,
        fontStyle: "bold",
        fontSize: 7,
        cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
      },
      alternateRowStyles: {
        fillColor: LIGHT_GRAY,
      },
      columnStyles: {
        section: { cellWidth: 18 },
        bidArea: { cellWidth: 20 },
        itemCode: { cellWidth: 22 },
        description: { cellWidth: "auto" },
        uom: { cellWidth: 12, halign: "center" },
        qty: { cellWidth: 18, halign: "right" },
        payRate: { cellWidth: 24, halign: "right" },
        subtotal: {
          cellWidth: 28,
          halign: "right",
          fontStyle: "bold",
          textColor: ACCENT,
        },
      },
      margin: { left: 10, right: 10 },
      tableLineColor: [220, 222, 224],
      tableLineWidth: 0.1,
    });

    const afterTable = doc.lastAutoTable.finalY + 4;

    // ── Pay summary footer ───────────────────────────────────────────────
    // Worker sees: Base Pay only.
    // We do NOT show burden on the worker sheet — it's an employer cost.
    const summaryBoxW = 90;
    const summaryBoxX = pageW - 10 - summaryBoxW;
    const summaryBoxY = afterTable;
    const lineH = 7;

    doc.setFillColor(...LIGHT_GRAY);
    doc.roundedRect(
      summaryBoxX,
      summaryBoxY,
      summaryBoxW,
      lineH * 2 + 2, // two rows: line count + base pay total
      2,
      2,
      "F",
    );

    const lineCount = lineItems.length;
    const summaryRows = [
      [`${lineCount} item${lineCount !== 1 ? "s" : ""}`, ""],
      ["Total Pay", `$${fmt(workerPayTotal)}`],
    ];

    summaryRows.forEach(([label, value], i) => {
      const rowY = summaryBoxY + 6 + i * lineH;
      const isTotal = i === summaryRows.length - 1;

      if (isTotal) {
        doc.setFillColor(...ACCENT);
        doc.roundedRect(
          summaryBoxX,
          summaryBoxY + lineH * 1 + 1,
          summaryBoxW,
          lineH + 1,
          1.5,
          1.5,
          "F",
        );
      }

      doc.setFont("helvetica", isTotal ? "bold" : "normal");
      doc.setFontSize(isTotal ? 9 : 8);
      doc.setTextColor(...(isTotal ? WHITE : DARK));

      doc.text(label, summaryBoxX + 5, rowY);
      if (value) {
        doc.text(value, summaryBoxX + summaryBoxW - 5, rowY, {
          align: "right",
        });
      }
    });

    // Notes
    if (log.notes) {
      const notesY = afterTable + 2;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(...MID_GRAY);
      doc.text("NOTES", 10, notesY + 4);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...DARK);
      const lines = doc.splitTextToSize(log.notes, summaryBoxX - 20);
      doc.text(lines, 10, notesY + 10);
    }

    // ── Page footer ──────────────────────────────────────────────────────
    doc.setFillColor(...BRAND);
    doc.rect(0, pageH - 8, pageW, 8, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(...WHITE);
    const genDate = new Date().toLocaleString("en-CA", {
      dateStyle: "medium",
      timeStyle: "short",
    });
    doc.text(`Generated ${genDate}  ·  ${projectName}`, 10, pageH - 3);
    doc.text(`${logIdx + 1} / ${logs.length}`, pageW - 10, pageH - 3, {
      align: "right",
    });
  });

  return doc;
};

// Parses a date-only value from an ISO string without timezone shifting.
// "2026-05-03T00:00:00.000Z" → "2026-05-03"  (always the stored calendar day)
const parseDateOnly = (iso) => {
  if (!iso) return null;
  return typeof iso === "string"
    ? iso.slice(0, 10)
    : new Date(iso).toISOString().slice(0, 10);
};

const fmtDateOnly = (iso) => {
  const d = parseDateOnly(iso);
  return d || "—";
};

const fmtDateDisplay = (iso) => {
  const d = parseDateOnly(iso);
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(day, 10)}, ${y}`;
};

export default function PieceworkerLogsTab3({ project }) {
  const projectId = project?._id;
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const STORAGE_KEY = `pwlogs3_filters_${projectId}`;

  const savedFilters = (() => {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
    } catch {
      return {};
    }
  })();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(
    savedFilters.statusFilter ?? "pending",
  );
  const [woFilter, setWoFilter] = useState(savedFilters.woFilter ?? "all");
  const [search, setSearch] = useState(savedFilters.search ?? "");
  const [page, setPage] = useState(savedFilters.page ?? 1);
  const [sortCol, setSortCol] = useState(savedFilters.sortCol ?? "logNumber");
  const [sortDir, setSortDir] = useState(savedFilters.sortDir ?? "asc");
  const [dateFrom, setDateFrom] = useState(savedFilters.dateFrom ?? "");
  const [dateTo, setDateTo] = useState(savedFilters.dateTo ?? "");
  const [batchActing, setBatchActing] = useState(false);
  // new state, next to the others:
  const [tagFilter, setTagFilter] = useState(savedFilters.tagFilter ?? []);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [batchRejecting, setBatchRejecting] = useState(false);
  const [rejectModal, setRejectModal] = useState({ open: false, reason: "" });
  const [selected, setSelected] = useState(new Set());
  const [pdfExporting, setPdfExporting] = useState(false);
  const LIMIT = 100;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const pageSize = 200;
      let pageNo = 1;
      let all = [];
      let total = Infinity;
      while (all.length < total) {
        const params = { project: projectId, limit: pageSize, page: pageNo };
        if (statusFilter !== "all") params.status = statusFilter;
        const res = await axios.get(`${API}/pieceworkerLogs3`, {
          headers,
          params,
        });
        const batch = res.data.data || [];
        all = all.concat(batch);
        total = res.data.pagination?.total ?? all.length;
        if (batch.length < pageSize) break;
        pageNo += 1;
      }
      setLogs(all);
    } catch (e) {
      console.error("Failed to load piecework logs:", e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId, statusFilter]);

  const handleRefreshTags = async () => {
    setSyncing(true);
    setSyncMsg("");
    try {
      const res = await axios.post(
        `${API}/pieceworkerLogs3/sync-tags`,
        { projectId },
        { headers },
      );
      await fetchLogs();
      const n = res.data?.updated ?? 0;
      setSyncMsg(
        n
          ? `Tags refreshed on ${n} log${n !== 1 ? "s" : ""}.`
          : "Logs already up to date.",
      );
    } catch (e) {
      setSyncMsg(e.response?.data?.message || e.message);
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMsg(""), 6000);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [statusFilter, woFilter, search, dateFrom, dateTo]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          statusFilter,
          woFilter,
          search,
          page,
          sortCol,
          sortDir,
          dateFrom,
          dateTo,
        }),
      );
    } catch {}
  }, [
    statusFilter,
    woFilter,
    search,
    page,
    sortCol,
    sortDir,
    dateFrom,
    dateTo,
  ]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const uniqueWOs = useMemo(() => {
    const map = new Map();
    logs.forEach((l) => {
      const wo = l.workOrder;
      if (wo && !map.has(wo._id || wo))
        map.set(wo._id || wo, wo.title || wo.woNumber || wo);
    });
    return [["all", "All Work Orders"], ...map.entries()];
  }, [logs]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo + "T23:59:59") : null;
    return logs.filter((l) => {
      if (woFilter !== "all") {
        const woId = l.workOrder?._id || l.workOrder;
        if (woId?.toString() !== woFilter) return false;
      }
      if (from || to) {
        const wd = l.workCompletedDate ? new Date(l.workCompletedDate) : null;
        if (!wd) return false;
        if (from && wd < from) return false;
        if (to && wd > to) return false;
      }
      if (tagFilter.length && !hasAllTags(l, tagFilter)) return false;
      if (q) {
        const worker = l.worker?.displayName || l.worker?.email || "";
        const wo = l.workOrder?.title || l.workOrder?.woNumber || "";
        const num = l.logNumber || "";
        const reqNum = l.sourceRequestId?.requestNumber || "";
        const hit =
          worker.toLowerCase().includes(q) ||
          wo.toLowerCase().includes(q) ||
          num.toLowerCase().includes(q) ||
          reqNum.toLowerCase().includes(q) ||
          matchesTextQuery(l, search);
        if (!hit) return false;
      }
      return true;
    });
  }, [logs, woFilter, search, dateFrom, dateTo, tagFilter]);

  const tagOptions = useMemo(() => buildTagOptions(logs), [logs]);

  const sorted = useMemo(() => {
    const dir = sortDir === "desc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (sortCol) {
        case "logNumber": {
          // numeric sort on the trailing digits, e.g. "PWL-0042" → 42
          const na = parseInt((a.logNumber || "").replace(/\D/g, ""), 10) || 0;
          const nb = parseInt((b.logNumber || "").replace(/\D/g, ""), 10) || 0;
          return (na - nb) * dir;
        }
        case "requestNumber": {
          const na =
            parseInt(
              (a.sourceRequestId?.requestNumber || "").replace(/\D/g, ""),
              10,
            ) || 0;
          const nb =
            parseInt(
              (b.sourceRequestId?.requestNumber || "").replace(/\D/g, ""),
              10,
            ) || 0;
          return (na - nb) * dir;
        }
        case "status":
          return (a.status || "").localeCompare(b.status || "") * dir;
        case "workOrder":
          return (
            (a.workOrder?.title || "").localeCompare(b.workOrder?.title || "") *
            dir
          );
        case "worker":
          return (
            (a.worker?.displayName || a.worker?.email || "").localeCompare(
              b.worker?.displayName || b.worker?.email || "",
            ) * dir
          );
        case "items":
          return (
            ((a.lineItems?.length || 0) - (b.lineItems?.length || 0)) * dir
          );
        case "workDate": {
          const da = a.workCompletedDate ? new Date(a.workCompletedDate) : 0;
          const db = b.workCompletedDate ? new Date(b.workCompletedDate) : 0;
          return (da - db) * dir;
        }
        case "submitted": {
          const da = a.submittedAt ? new Date(a.submittedAt) : 0;
          const db = b.submittedAt ? new Date(b.submittedAt) : 0;
          return (da - db) * dir;
        }
        case "subtotal":
          return (logSubtotal(a) - logSubtotal(b)) * dir;
        case "totalCost":
          return ((a.totalCost || 0) - (b.totalCost || 0)) * dir;
        default:
          return 0;
      }
    });
  }, [filtered, sortCol, sortDir]);

  const paginated = useMemo(() => {
    const start = (page - 1) * LIMIT;
    return sorted.slice(start, start + LIMIT);
  }, [sorted, page]);

  const totalPages = Math.ceil(sorted.length / LIMIT);

  const summary = useMemo(
    () => ({
      pending: logs.filter((l) => l.status === "pending").length,
      approved: logs.filter((l) => l.status === "approved").length,
      rejected: logs.filter((l) => l.status === "rejected").length,
      totalCost: logs
        .filter((l) => l.status === "approved")
        .reduce((s, l) => s + (l.totalCost || 0), 0),
      totalSubtotal: logs
        .filter((l) => l.status === "approved")
        .reduce((s, l) => s + logSubtotal(l), 0),
      totalBurden: logs
        .filter((l) => l.status === "approved")
        .reduce((s, l) => s + logBurden(l), 0),
    }),
    [logs],
  );

  const handleItemizedExport = () => {
    const logsToExport =
      selected.size > 0 ? logs.filter((l) => selected.has(l._id)) : filtered;
    if (logsToExport.length === 0) return;

    const projectName =
      project?.projectName || project?.projectNumber || "Project";
    const dateStr = new Date().toISOString().split("T")[0];
    const moneyFmt = "#,##0.00";
    const qtyFmt = "#,##0.####";

    const woLabel = (log) => {
      const title = log.workOrder?.title || "";
      const num = log.workOrder?.woNumber || "";
      if (title && num) return `${title} (${num})`;
      return title || num || "—";
    };

    // ── Shared style helpers ──────────────────────────────────────────────────
    const headerStyle = (bg = "1F2937") => ({
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: bg } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        bottom: { style: "thin", color: { rgb: "4B5563" } },
      },
    });

    const totalRowStyle = (fg = "F97316") => ({
      font: { bold: true, color: { rgb: fg } },
      fill: { fgColor: { rgb: "1F2937" } },
      alignment: { horizontal: "right" },
      border: {
        top: { style: "thin", color: { rgb: "4B5563" } },
      },
    });

    const applyMoneyCells = (ws, rowIdx, colIndices) => {
      colIndices.forEach((c) => {
        const addr = XLSX.utils.encode_cell({ r: rowIdx, c });
        if (ws[addr]) ws[addr].z = moneyFmt;
      });
    };

    const applyQtyCells = (ws, rowIdx, colIndices) => {
      colIndices.forEach((c) => {
        const addr = XLSX.utils.encode_cell({ r: rowIdx, c });
        if (ws[addr]) ws[addr].z = qtyFmt;
      });
    };

    const applyStyle = (ws, rowIdx, colIndices, style) => {
      colIndices.forEach((c) => {
        const addr = XLSX.utils.encode_cell({ r: rowIdx, c });
        if (!ws[addr]) ws[addr] = { t: "z", v: "" };
        ws[addr].s = style;
      });
    };

    // ── Build intermediate data structures ────────────────────────────────────
    // Map: workerId → { workerName, items: Map<itemKey, aggregated> }
    const byWorker = new Map();
    // Map: itemKey → { workOrder, itemCode, description, uom, unitRate, workers: Map<workerId, aggregated> }
    const byItem = new Map();

    for (const log of logsToExport) {
      const workerId = log.worker?._id || log.worker || "unknown";
      const workerName =
        log.worker?.displayName || log.worker?.email || "Unknown";
      const woName = woLabel(log);
      const logNum = log.logNumber || "—";
      const logStatus = log.status || "pending";
      const workDate = fmtDateOnly(log.workCompletedDate);

      if (!byWorker.has(workerId)) {
        byWorker.set(workerId, { workerName, items: new Map() });
      }
      const workerEntry = byWorker.get(workerId);

      for (const li of log.lineItems || []) {
        const code = li.itemCode || "—";
        const desc = li.description || "—";
        const uom = li.uom || "—";
        const rate = li.unitRate || 0;
        const qty = li.quantity || 0;
        const sub = li.subtotal || 0;
        const burden = li.burden || 0;
        const total = li.total || sub + burden;
        // Key includes work order so different WOs stay separate
        const itemKey = `${woName}||${code}||${rate}`;

        // Worker → Item
        if (!workerEntry.items.has(itemKey)) {
          workerEntry.items.set(itemKey, {
            workOrder: woName,
            itemCode: code,
            description: desc,
            uom,
            unitRate: rate,
            rateSource: li.rateSource || "",
            totalQty: 0,
            totalSubtotal: 0,
            totalBurden: 0,
            totalCost: 0,
            logs: [],
          });
        }
        const wItem = workerEntry.items.get(itemKey);
        wItem.totalQty += qty;
        wItem.totalSubtotal += sub;
        wItem.totalBurden += burden;
        wItem.totalCost += total;
        wItem.logs.push({
          logNum,
          logStatus,
          workDate,
          qty,
          subtotal: sub,
          burden,
          total,
        });

        // Item → Worker
        if (!byItem.has(itemKey)) {
          byItem.set(itemKey, {
            workOrder: woName,
            itemCode: code,
            description: desc,
            uom,
            unitRate: rate,
            rateSource: li.rateSource || "",
            workers: new Map(),
          });
        }
        const itemEntry = byItem.get(itemKey);
        if (!itemEntry.workers.has(workerId)) {
          itemEntry.workers.set(workerId, {
            workerName,
            totalQty: 0,
            totalSubtotal: 0,
            totalBurden: 0,
            totalCost: 0,
            logs: [],
          });
        }
        const iWorker = itemEntry.workers.get(workerId);
        iWorker.totalQty += qty;
        iWorker.totalSubtotal += sub;
        iWorker.totalBurden += burden;
        iWorker.totalCost += total;
        iWorker.logs.push({
          logNum,
          logStatus,
          workDate,
          qty,
          subtotal: sub,
          burden,
          total,
        });
      }
    }

    const wb = XLSX.utils.book_new();

    // ════════════════════════════════════════════════════════════════════════════
    // SHEET 1 — Summary by Worker
    // ════════════════════════════════════════════════════════════════════════════
    {
      const headers = [
        "Worker",
        "# Logs",
        "# Line Items",
        "Total Qty",
        "Base Pay (Subtotal)",
        "Burden",
        "Total Cost",
      ];
      const rows = [];

      for (const { workerName, items } of byWorker.values()) {
        const logSet = new Set();
        let totalQty = 0,
          totalSub = 0,
          totalBurden = 0,
          totalCost = 0,
          lineCount = 0;

        for (const item of items.values()) {
          lineCount++;
          totalQty += item.totalQty;
          totalSub += item.totalSubtotal;
          totalBurden += item.totalBurden;
          totalCost += item.totalCost;
          item.logs.forEach((l) => logSet.add(l.logNum));
        }

        rows.push({
          Worker: workerName,
          "# Logs": logSet.size,
          "# Line Items": lineCount,
          "Total Qty": totalQty,
          "Base Pay (Subtotal)": totalSub,
          Burden: totalBurden,
          "Total Cost": totalCost,
        });
      }

      rows.push({
        Worker: `TOTAL (${byWorker.size} worker${byWorker.size !== 1 ? "s" : ""})`,
        "# Logs": logsToExport.length,
        "# Line Items": rows.reduce((s, r) => s + r["# Line Items"], 0),
        "Total Qty": rows.reduce((s, r) => s + r["Total Qty"], 0),
        "Base Pay (Subtotal)": rows.reduce(
          (s, r) => s + r["Base Pay (Subtotal)"],
          0,
        ),
        Burden: rows.reduce((s, r) => s + r["Burden"], 0),
        "Total Cost": rows.reduce((s, r) => s + r["Total Cost"], 0),
      });

      const ws1 = XLSX.utils.json_to_sheet(rows);
      ws1["!cols"] = [
        { wch: 28 },
        { wch: 8 },
        { wch: 12 },
        { wch: 12 },
        { wch: 22 },
        { wch: 14 },
        { wch: 16 },
      ];

      for (let c = 0; c < headers.length; c++) {
        const addr = XLSX.utils.encode_cell({ r: 0, c });
        if (ws1[addr]) ws1[addr].s = headerStyle();
      }
      for (let r = 1; r <= rows.length; r++) {
        const isTotal = r === rows.length;
        const style = isTotal
          ? totalRowStyle()
          : { fill: { fgColor: { rgb: r % 2 === 0 ? "F9FAFB" : "FFFFFF" } } };
        for (let c = 0; c < headers.length; c++) {
          applyStyle(ws1, r, [c], style);
        }
        applyMoneyCells(ws1, r, [4, 5, 6]);
        applyQtyCells(ws1, r, [3]);
      }

      XLSX.utils.book_append_sheet(wb, ws1, "Summary by Worker");
    }

    // ════════════════════════════════════════════════════════════════════════════
    // SHEET 2 — By Worker → Work Order → Item Code (flat filterable table)
    // ════════════════════════════════════════════════════════════════════════════
    {
      const headers = [
        "Worker", // 0
        "Work Order", // 1
        "Item Code", // 2
        "Description", // 3
        "UOM", // 4
        "Unit Rate", // 5
        "Eff. Pay Rate", // 6
        "Burden %", // 7
        "Eff. Total Rate", // 8
        "Rate Source", // 9
        "Total Qty", // 10
        "Base Pay", // 11
        "Burden", // 12
        "Total Cost", // 13
        "Log References", // 14
      ];

      const rows = [];

      for (const { workerName, items } of byWorker.values()) {
        for (const item of items.values()) {
          const logRefs = item.logs
            .map((l) => `${l.logNum} (${l.workDate}, ${l.logStatus})`)
            .join(" | ");

          const effPayRate =
            item.totalQty > 0 ? item.totalSubtotal / item.totalQty : 0;
          const burdenPct =
            item.totalSubtotal > 0 ? item.totalBurden / item.totalSubtotal : 0;
          const effTotalRate =
            item.totalQty > 0 ? item.totalCost / item.totalQty : 0;

          rows.push([
            workerName,
            item.workOrder,
            item.itemCode,
            item.description,
            item.uom,
            item.unitRate,
            effPayRate,
            burdenPct,
            effTotalRate,
            item.rateSource,
            item.totalQty,
            item.totalSubtotal,
            item.totalBurden,
            item.totalCost,
            logRefs,
          ]);
        }
      }

      rows.sort(
        (a, b) =>
          a[0].localeCompare(b[0]) ||
          a[1].localeCompare(b[1]) ||
          a[2].localeCompare(b[2]),
      );

      const aoa = [headers, ...rows];
      const numCols = headers.length;

      const ws2 = XLSX.utils.aoa_to_sheet(aoa);
      ws2["!cols"] = [
        { wch: 26 },
        { wch: 26 },
        { wch: 16 },
        { wch: 34 },
        { wch: 8 },
        { wch: 13 },
        { wch: 14 },
        { wch: 10 },
        { wch: 15 },
        { wch: 12 },
        { wch: 12 },
        { wch: 16 },
        { wch: 14 },
        { wch: 16 },
        { wch: 55 },
      ];

      ws2["!autofilter"] = {
        ref: XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: 0, c: numCols - 1 },
        }),
      };

      for (let c = 0; c < numCols; c++) applyStyle(ws2, 0, [c], headerStyle());
      [5, 6, 7, 8].forEach((c) => {
        applyStyle(ws2, 0, [c], headerStyle("7C3AED"));
      });

      for (let r = 1; r < aoa.length; r++) {
        const even = r % 2 === 0;
        const baseStyle = {
          fill: { fgColor: { rgb: even ? "EFF6FF" : "FFFFFF" } },
          alignment: { horizontal: "left", vertical: "center" },
        };
        for (let c = 0; c < numCols; c++) applyStyle(ws2, r, [c], baseStyle);

        [5, 6, 7, 8, 10, 11, 12, 13].forEach((c) => {
          const addr = XLSX.utils.encode_cell({ r, c });
          if (ws2[addr])
            ws2[addr].s = {
              ...ws2[addr].s,
              alignment: { horizontal: "right" },
            };
        });

        applyMoneyCells(ws2, r, [5, 6, 8, 11, 12, 13]);
        applyQtyCells(ws2, r, [10]);

        const pctAddr = XLSX.utils.encode_cell({ r, c: 7 });
        if (ws2[pctAddr]) ws2[pctAddr].z = "0.00%";

        [5, 6, 7, 8].forEach((c) => {
          const addr = XLSX.utils.encode_cell({ r, c });
          if (ws2[addr])
            ws2[addr].s = {
              ...ws2[addr].s,
              fill: { fgColor: { rgb: even ? "EDE9FE" : "F5F3FF" } },
            };
        });
      }

      XLSX.utils.book_append_sheet(wb, ws2, "By Worker \u2192 Item Code");
    }

    // ════════════════════════════════════════════════════════════════════════════
    // SHEET 3 — Work Order → Item Code → Worker breakdown (flat filterable table)
    // ════════════════════════════════════════════════════════════════════════════
    {
      const headers = [
        "Work Order", // 0
        "Item Code", // 1
        "Description", // 2
        "UOM", // 3
        "Unit Rate", // 4
        "Eff. Pay Rate", // 5
        "Burden %", // 6
        "Eff. Total Rate", // 7
        "Rate Source", // 8
        "Worker", // 9
        "Total Qty", // 10
        "Base Pay", // 11
        "Burden", // 12
        "Total Cost", // 13
        "Log References", // 14
      ];

      const sortedItems = [...byItem.values()].sort(
        (a, b) =>
          a.workOrder.localeCompare(b.workOrder) ||
          a.itemCode.localeCompare(b.itemCode),
      );

      const rows3 = [];
      for (const item of sortedItems) {
        for (const worker of item.workers.values()) {
          const logRefs = worker.logs
            .map((l) => `${l.logNum} (${l.workDate}, ${l.logStatus})`)
            .join(" | ");

          const effPayRate =
            worker.totalQty > 0 ? worker.totalSubtotal / worker.totalQty : 0;
          const burdenPct =
            worker.totalSubtotal > 0
              ? worker.totalBurden / worker.totalSubtotal
              : 0;
          const effTotalRate =
            worker.totalQty > 0 ? worker.totalCost / worker.totalQty : 0;

          rows3.push([
            item.workOrder,
            item.itemCode,
            item.description,
            item.uom,
            item.unitRate,
            effPayRate,
            burdenPct,
            effTotalRate,
            item.rateSource,
            worker.workerName,
            worker.totalQty,
            worker.totalSubtotal,
            worker.totalBurden,
            worker.totalCost,
            logRefs,
          ]);
        }
      }

      rows3.sort(
        (a, b) =>
          a[0].localeCompare(b[0]) ||
          a[1].localeCompare(b[1]) ||
          a[9].localeCompare(b[9]),
      );

      const aoa3 = [headers, ...rows3];
      const numCols3 = headers.length;

      const ws3 = XLSX.utils.aoa_to_sheet(aoa3);
      ws3["!cols"] = [
        { wch: 26 },
        { wch: 16 },
        { wch: 34 },
        { wch: 8 },
        { wch: 13 },
        { wch: 14 },
        { wch: 10 },
        { wch: 15 },
        { wch: 12 },
        { wch: 26 },
        { wch: 12 },
        { wch: 16 },
        { wch: 14 },
        { wch: 16 },
        { wch: 55 },
      ];

      ws3["!autofilter"] = {
        ref: XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: 0, c: numCols3 - 1 },
        }),
      };

      for (let c = 0; c < numCols3; c++) applyStyle(ws3, 0, [c], headerStyle());
      [4, 5, 6, 7].forEach((c) =>
        applyStyle(ws3, 0, [c], headerStyle("7C3AED")),
      );

      for (let r = 1; r < aoa3.length; r++) {
        const even = r % 2 === 0;
        const baseStyle = {
          fill: { fgColor: { rgb: even ? "F0FDF4" : "FFFFFF" } },
          alignment: { horizontal: "left", vertical: "center" },
        };
        for (let c = 0; c < numCols3; c++) applyStyle(ws3, r, [c], baseStyle);

        [4, 5, 6, 7, 10, 11, 12, 13].forEach((c) => {
          const addr = XLSX.utils.encode_cell({ r, c });
          if (ws3[addr])
            ws3[addr].s = {
              ...ws3[addr].s,
              alignment: { horizontal: "right" },
            };
        });

        applyMoneyCells(ws3, r, [4, 5, 7, 11, 12, 13]);
        applyQtyCells(ws3, r, [10]);

        const pctAddr = XLSX.utils.encode_cell({ r, c: 6 });
        if (ws3[pctAddr]) ws3[pctAddr].z = "0.00%";

        [4, 5, 6, 7].forEach((c) => {
          const addr = XLSX.utils.encode_cell({ r, c });
          if (ws3[addr])
            ws3[addr].s = {
              ...ws3[addr].s,
              fill: { fgColor: { rgb: even ? "EDE9FE" : "F5F3FF" } },
            };
        });
      }

      XLSX.utils.book_append_sheet(
        wb,
        ws3,
        "By Work Order \u2192 Item \u2192 Worker",
      );
    }

    // ── Save ──────────────────────────────────────────────────────────────────
    const statusLabel = statusFilter !== "all" ? `_${statusFilter}` : "";
    const selLabel = selected.size > 0 ? `_selected${selected.size}` : "";
    XLSX.writeFile(
      wb,
      `${projectName}_PieceworkItemized${statusLabel}${selLabel}_${dateStr}.xlsx`,
    );
  };

  // ── Excel export ──────────────────────────────────────────────────────────
  const handleExport = () => {
    const projectName =
      project?.projectName || project?.projectNumber || "Project";
    const rows = filtered.map((log) => {
      const sub = logSubtotal(log);
      const burden = logBurden(log);
      return {
        "Log #": log.logNumber || "",
        Status: log.status
          ? log.status.charAt(0).toUpperCase() + log.status.slice(1)
          : "",
        "Work Order": log.workOrder?.title || "",
        "WO Number": log.workOrder?.woNumber || "",
        Worker: log.worker?.displayName || log.worker?.email || "",
        "Line Items": (log.lineItems || []).length,
        "Work Date": fmtDateOnly(log.workCompletedDate),
        Submitted: log.submittedAt
          ? new Date(log.submittedAt).toLocaleDateString("en-CA")
          : "",
        "Subtotal (Base Pay)": sub,
        Burden: burden,
        "Total Cost": log.totalCost || 0,
        Notes: log.notes || "",
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 14 },
      { wch: 11 },
      { wch: 28 },
      { wch: 14 },
      { wch: 22 },
      { wch: 10 },
      { wch: 13 },
      { wch: 13 },
      { wch: 20 },
      { wch: 14 },
      { wch: 14 },
      { wch: 30 },
    ];

    const headerRange = XLSX.utils.decode_range(ws["!ref"]);
    for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
      const cellAddr = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!ws[cellAddr]) continue;
      ws[cellAddr].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "1F2937" } },
        alignment: { horizontal: "center" },
      };
    }

    const moneyFmt = "#,##0.00";
    for (let r = 1; r <= rows.length; r++) {
      [8, 9, 10].forEach((col) => {
        const cellAddr = XLSX.utils.encode_cell({ r, c: col });
        if (ws[cellAddr]) ws[cellAddr].z = moneyFmt;
      });
    }

    const totalsRow = {
      "Log #": `${filtered.length} log${filtered.length !== 1 ? "s" : ""}`,
      Status: "",
      "Work Order": "",
      "WO Number": "",
      Worker: "",
      "Line Items": filtered.reduce(
        (s, l) => s + (l.lineItems || []).length,
        0,
      ),
      "Work Date": "",
      Submitted: "",
      "Subtotal (Base Pay)": filtered.reduce((s, l) => s + logSubtotal(l), 0),
      Burden: filtered.reduce((s, l) => s + logBurden(l), 0),
      "Total Cost": filtered.reduce((s, l) => s + (l.totalCost || 0), 0),
      Notes: "",
    };
    XLSX.utils.sheet_add_json(ws, [totalsRow], {
      skipHeader: true,
      origin: rows.length + 1,
    });

    const totalsRowIdx = rows.length + 1;
    for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
      const cellAddr = XLSX.utils.encode_cell({ r: totalsRowIdx, c: col });
      if (!ws[cellAddr]) continue;
      ws[cellAddr].s = { font: { bold: true } };
      if ([8, 9, 10].includes(col)) ws[cellAddr].z = moneyFmt;
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Piecework Logs");

    const dateStr = new Date().toISOString().split("T")[0];
    const statusLabel = statusFilter !== "all" ? `_${statusFilter}` : "";
    XLSX.writeFile(
      wb,
      `${projectName}_PieceworkLogs${statusLabel}_${dateStr}.xlsx`,
    );
  };

  // ── PDF export — zip organized by worker ─────────────────────────────────
  // Each worker gets a folder. Inside: one PDF per log, named by log number.
  // If only one worker exists, skips the zip and saves a single PDF directly.
  const handlePdfExport = async () => {
    const logsToExport =
      selected.size > 0 ? logs.filter((l) => selected.has(l._id)) : filtered;

    if (logsToExport.length === 0) return;
    setPdfExporting(true);

    try {
      const projectName =
        project?.projectName || project?.projectNumber || "Project";
      const dateStr = new Date().toISOString().split("T")[0];

      const safe = (s) =>
        (s || "Unknown").replace(/[/\\?%*:|"<>]/g, "-").trim();

      // Group logs by worker
      const byWorker = new Map();
      for (const log of logsToExport) {
        const workerId = log.worker?._id || log.worker || "unknown";
        const workerName =
          log.worker?.displayName || log.worker?.email || "Unknown Worker";
        if (!byWorker.has(workerId))
          byWorker.set(workerId, { name: workerName, logs: [] });
        byWorker.get(workerId).logs.push(log);
      }

      // Sort each worker's logs by work date ascending
      for (const entry of byWorker.values()) {
        entry.logs.sort((a, b) => {
          const da = a.workCompletedDate ? new Date(a.workCompletedDate) : 0;
          const db = b.workCompletedDate ? new Date(b.workCompletedDate) : 0;
          return da - db;
        });
      }

      // Single worker — save one combined PDF directly, no zip
      if (byWorker.size === 1) {
        const [{ name, logs: wLogs }] = byWorker.values();
        const doc = buildPDF(wLogs, project);
        doc.save(`${safe(projectName)}_${safe(name)}_${dateStr}.pdf`);
        return;
      }

      // Multiple workers — one combined PDF per worker, zipped together
      const zip = new JSZip();

      for (const { name, logs: wLogs } of byWorker.values()) {
        // buildPDF already handles multi-log → multi-page in one document
        const doc = buildPDF(wLogs, project);
        const pdfBytes = doc.output("arraybuffer");
        zip.file(`${safe(name)}.pdf`, pdfBytes);
      }

      const suffix =
        selected.size > 0
          ? `_selected`
          : statusFilter !== "all"
            ? `_${statusFilter}`
            : "";

      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safe(projectName)}_PieceworkSheets${suffix}_${dateStr}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("PDF export error:", e);
    } finally {
      setPdfExporting(false);
    }
  };

  // ── Batch approve ─────────────────────────────────────────────────────────
  const batchApprove = async () => {
    if (selected.size === 0) return;
    setBatchActing(true);
    const pending = [...selected].filter(
      (id) => logs.find((l) => l._id === id)?.status === "pending",
    );
    try {
      await Promise.all(
        pending.map((id) =>
          axios.post(`${API}/pieceworkerLogs3/${id}/approve`, {}, { headers }),
        ),
      );
      setSelected(new Set());
      await fetchLogs();
    } catch (e) {
      console.error("Batch approve error:", e.message);
    } finally {
      setBatchActing(false);
    }
  };

  const batchReject = async () => {
    const pending = [...selected].filter(
      (id) => logs.find((l) => l._id === id)?.status === "pending",
    );
    if (!pending.length) return;
    setBatchRejecting(true);
    try {
      await Promise.all(
        pending.map((id) =>
          axios.post(
            `${API}/pieceworkerLogs3/${id}/reject`,
            { reason: rejectModal.reason },
            { headers },
          ),
        ),
      );
      setSelected(new Set());
      setRejectModal({ open: false, reason: "" });
      await fetchLogs();
    } catch (e) {
      console.error("Batch reject error:", e.message);
    } finally {
      setBatchRejecting(false);
    }
  };

  const toggleSelect = (id) =>
    setSelected((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const selectAll = () =>
    setSelected(
      new Set(
        paginated.filter((l) => l.status === "pending").map((l) => l._id),
      ),
    );
  const clearAll = () => setSelected(new Set());

  const pendingSelected = [...selected].filter(
    (id) => logs.find((l) => l._id === id)?.status === "pending",
  ).length;

  const pdfCount = selected.size > 0 ? selected.size : filtered.length;

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
    setPage(1);
  };

  const SortHeader = ({ col, children, className = "" }) => {
    const active = sortCol === col;
    return (
      <th
        onClick={() => handleSort(col)}
        className={`px-3 py-2.5 text-left text-gray-400 font-medium cursor-pointer select-none hover:text-gray-200 transition-colors group ${className}`}
      >
        <span className="inline-flex items-center gap-1">
          {children}
          <span
            className={`text-[10px] transition-opacity ${active ? "opacity-100" : "opacity-0 group-hover:opacity-40"}`}
          >
            {active ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
          </span>
        </span>
      </th>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Summary row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          {
            label: "Pending",
            value: summary.pending,
            color: "text-yellow-400",
          },
          {
            label: "Approved",
            value: summary.approved,
            color: "text-green-400",
          },
          { label: "Rejected", value: summary.rejected, color: "text-red-400" },
          {
            label: "Approved Subtotal",
            value: `$${fmt(summary.totalSubtotal)}`,
            color: "text-gray-200",
            str: true,
          },
          {
            label: "Approved Total",
            value: `$${fmt(summary.totalCost)}`,
            color: "text-orange-400",
            str: true,
          },
        ].map(({ label, value, color, str }) => (
          <div
            key={label}
            className="bg-gray-800 border border-gray-700 rounded-xl px-4 py-3"
          >
            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
              {label}
            </p>
            <p
              className={cls("text-xl font-bold tabular-nums font-mono", color)}
            >
              {str ? value : value}
            </p>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        {/* <button
          onClick={() =>
            navigate(`/projects/${projectId}/pieceworker-logs3/new`, {
              state: { projectId },
            })
          }
          className="px-3 py-1.5 text-sm font-medium text-white border border-green-700 bg-green-900 rounded-lg hover:bg-green-900/80 transition-colors"
        >
          + New Piecework Log
        </button> */}

        {/* Status filter */}
        <div className="flex rounded-lg overflow-hidden border border-gray-600">
          {["all", "pending", "approved", "rejected"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={cls(
                "px-3 py-1.5 text-xs font-medium capitalize",
                statusFilter === s
                  ? "bg-orange-500 text-white"
                  : "bg-gray-700 text-gray-400 hover:bg-gray-600",
              )}
            >
              {s === "all" ? "All" : s}
            </button>
          ))}
        </div>

        {/* WO filter */}
        {uniqueWOs.length > 2 && (
          <select
            value={woFilter}
            onChange={(e) => setWoFilter(e.target.value)}
            className="px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:border-orange-500 focus:outline-none"
          >
            {uniqueWOs.map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        )}

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search worker, WO, log #, unit, notes…"
          className="px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none w-52"
        />

        {/* <button
          onClick={handleRefreshTags}
          disabled={syncing || loading}
          title="Reload logs and copy units and tags from their source requests"
          className="px-3 py-1.5 text-xs font-medium text-orange-300 bg-orange-900/30 border border-orange-700/50 rounded-lg hover:bg-orange-900/50 disabled:opacity-40 transition-colors"
        >
          {syncing ? "Refreshing…" : "⚡ Refresh & Tag"}
        </button> */}
        {syncMsg && <span className="text-xs text-green-400">✓ {syncMsg}</span>}

        {/* Work date range */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500 whitespace-nowrap">
            Work date
          </span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-2 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:border-orange-500 focus:outline-none"
            title="From date (work completed)"
          />
          <span className="text-xs text-gray-600">–</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            min={dateFrom || undefined}
            className="px-2 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:border-orange-500 focus:outline-none"
            title="To date (work completed)"
          />
          {(dateFrom || dateTo) && (
            <button
              onClick={() => {
                setDateFrom("");
                setDateTo("");
              }}
              className="text-xs text-gray-500 hover:text-gray-300 px-1"
              title="Clear date filter"
            >
              ✕
            </button>
          )}
        </div>

        <span className="text-xs text-gray-500 ml-auto">
          {filtered.length} log{filtered.length !== 1 ? "s" : ""}
        </span>

        {/* Excel export */}
        <button
          onClick={handleExport}
          disabled={filtered.length === 0}
          className={cls(
            "px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors flex items-center gap-1.5",
            filtered.length === 0
              ? "opacity-40 cursor-not-allowed bg-gray-700 border-gray-600 text-gray-400"
              : "bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 hover:text-white",
          )}
          title={`Export ${filtered.length} logs to Excel`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-3.5 h-3.5 text-green-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
          Export {filtered.length > 0 ? `(${filtered.length})` : ""}
        </button>

        <button
          onClick={handleItemizedExport}
          disabled={filtered.length === 0}
          className={cls(
            "px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors flex items-center gap-1.5",
            filtered.length === 0
              ? "opacity-40 cursor-not-allowed bg-gray-700 border-gray-600 text-gray-400"
              : "bg-gray-700 border-green-700/50 text-gray-300 hover:bg-gray-600 hover:text-white hover:border-green-600",
          )}
          title={
            selected.size > 0
              ? `Export itemized breakdown for ${selected.size} selected logs`
              : `Export itemized breakdown for all ${filtered.length} filtered logs`
          }
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-3.5 h-3.5 text-green-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
            <path
              fillRule="evenodd"
              d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
              clipRule="evenodd"
            />
          </svg>
          Itemized{" "}
          {selected.size > 0
            ? `(${selected.size} sel.)`
            : `(${filtered.length})`}
        </button>

        {/* PDF export */}
        <button
          onClick={handlePdfExport}
          disabled={pdfCount === 0 || pdfExporting}
          className={cls(
            "px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors flex items-center gap-1.5",
            pdfCount === 0 || pdfExporting
              ? "opacity-40 cursor-not-allowed bg-gray-700 border-gray-600 text-gray-400"
              : "bg-gray-700 border-red-700/50 text-gray-300 hover:bg-gray-600 hover:text-white hover:border-red-600",
          )}
          title={
            selected.size > 0
              ? `Export ${selected.size} selected log${selected.size !== 1 ? "s" : ""} as PDF worksheets`
              : `Export all ${filtered.length} filtered logs as PDF worksheets`
          }
        >
          {pdfExporting ? (
            <svg
              className="w-3.5 h-3.5 text-red-400 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-3.5 h-3.5 text-red-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                clipRule="evenodd"
              />
            </svg>
          )}
          {pdfExporting
            ? "Generating…"
            : `PDF ${selected.size > 0 ? `(${selected.size} selected)` : `(${filtered.length})`}`}
        </button>

        {/* Batch controls */}
        {/* Batch controls */}
        {(statusFilter === "pending" || statusFilter === "all") && (
          <div className="flex items-center gap-2">
            {selected.size === 0 ? (
              <button
                onClick={selectAll}
                className="px-3 py-1.5 text-xs text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600"
              >
                Select pending
              </button>
            ) : (
              <>
                <span className="text-xs text-gray-400">
                  {selected.size} selected
                </span>
                <button
                  onClick={clearAll}
                  className="px-2 py-1.5 text-xs text-gray-400 hover:text-gray-200"
                >
                  Clear
                </button>
                {pendingSelected > 0 && (
                  <>
                    <button
                      onClick={batchApprove}
                      disabled={batchActing}
                      className={cls(
                        "px-4 py-1.5 text-xs font-semibold text-white rounded-lg transition-colors",
                        batchActing
                          ? "bg-gray-600 cursor-not-allowed"
                          : "bg-green-600 hover:bg-green-700",
                      )}
                    >
                      {batchActing
                        ? "Approving…"
                        : `Approve ${pendingSelected}`}
                    </button>
                    <button
                      onClick={() => setRejectModal({ open: true, reason: "" })}
                      disabled={batchActing}
                      className={cls(
                        "px-4 py-1.5 text-xs font-semibold text-white rounded-lg transition-colors",
                        batchActing
                          ? "bg-gray-600 cursor-not-allowed"
                          : "bg-red-700 hover:bg-red-800",
                      )}
                    >
                      Reject {pendingSelected}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {tagOptions.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
              Tags:
            </span>
            {tagOptions.map(({ tag, count }) => {
              const on = tagFilter.includes(tag);
              return (
                <button
                  key={tag}
                  onClick={() =>
                    setTagFilter((p) =>
                      on ? p.filter((x) => x !== tag) : [...p, tag],
                    )
                  }
                  className={cls(
                    "px-2 py-0.5 text-[11px] rounded-full border transition-colors",
                    on
                      ? "bg-orange-500/20 border-orange-500/50 text-orange-300"
                      : "bg-gray-800 border-gray-700 text-gray-400 hover:text-gray-200",
                  )}
                >
                  #{tag} <span className="opacity-60">{count}</span>
                </button>
              );
            })}
            {tagFilter.length > 0 && (
              <button
                onClick={() => setTagFilter([])}
                className="text-[11px] text-orange-400 hover:text-orange-300 ml-1"
              >
                Clear
              </button>
            )}
          </div>
        )}

        {/* Bulk reject modal */}
        {rejectModal.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
              <h3 className="text-sm font-semibold text-white mb-1">
                Reject {pendingSelected} log{pendingSelected !== 1 ? "s" : ""}
              </h3>
              <p className="text-xs text-gray-400 mb-4">
                This will reject all selected pending logs and notify the
                originating requests so workers can see and resubmit.
              </p>
              <label className="block text-xs text-gray-400 mb-1.5">
                Reason <span className="text-gray-600">(optional)</span>
              </label>
              <textarea
                value={rejectModal.reason}
                onChange={(e) =>
                  setRejectModal((m) => ({ ...m, reason: e.target.value }))
                }
                placeholder="e.g. Quantities not verified, please resubmit with foreman sign-off"
                rows={3}
                className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-red-500 focus:outline-none resize-none"
              />
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => setRejectModal({ open: false, reason: "" })}
                  disabled={batchRejecting}
                  className="px-4 py-1.5 text-xs text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={batchReject}
                  disabled={batchRejecting}
                  className={cls(
                    "px-4 py-1.5 text-xs font-semibold text-white rounded-lg transition-colors",
                    batchRejecting
                      ? "bg-gray-600 cursor-not-allowed"
                      : "bg-red-700 hover:bg-red-800",
                  )}
                >
                  {batchRejecting
                    ? "Rejecting…"
                    : `Confirm Reject ${pendingSelected}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
            <span className="ml-3 text-sm text-gray-400">Loading…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-gray-500 text-sm">
            {logs.length === 0
              ? "No piecework logs for this project yet."
              : "No logs match your filters."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-3 py-2.5 w-8">
                    <input
                      type="checkbox"
                      checked={
                        selected.size > 0 &&
                        paginated
                          .filter((l) => l.status === "pending")
                          .every((l) => selected.has(l._id))
                      }
                      onChange={() =>
                        selected.size > 0 ? clearAll() : selectAll()
                      }
                      className="rounded border-gray-500 bg-gray-700"
                    />
                  </th>
                  <SortHeader col="logNumber">Log #</SortHeader>
                  <SortHeader col="requestNumber">Request #</SortHeader>
                  <SortHeader col="status">Status</SortHeader>
                  <SortHeader col="workOrder">Work Order</SortHeader>
                  <SortHeader col="worker">Worker</SortHeader>
                  <SortHeader col="items" className="text-right">
                    Items
                  </SortHeader>
                  <SortHeader col="workDate">Work Date</SortHeader>
                  <SortHeader col="submitted">Submitted</SortHeader>
                  <th className="px-3 py-2.5 text-left text-gray-400 font-medium">
                    Approved By
                  </th>
                  <th className="px-3 py-2.5 text-left text-gray-400 font-medium min-w-[160px]">
                    Notes
                  </th>
                  <SortHeader col="subtotal" className="text-right">
                    Subtotal
                    <span className="block text-[9px] text-gray-600 font-normal">
                      base pay
                    </span>
                  </SortHeader>
                  <SortHeader col="totalCost" className="text-right">
                    Total Cost
                  </SortHeader>
                  <th className="px-3 py-2.5 text-right text-gray-400 font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {paginated.map((log, idx) => {
                  const isPending = log.status === "pending";
                  const isChecked = selected.has(log._id);
                  const logProjectId = log.project?._id || log.project;
                  const sub = logSubtotal(log);
                  const hasBurden = (log.totalCost || 0) - sub > 0.005;
                  return (
                    <tr
                      key={log._id}
                      className={cls(
                        idx % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20",
                        isChecked && "bg-orange-900/10",
                        "hover:bg-gray-600/20 transition-colors",
                      )}
                    >
                      <td className="px-3 py-2.5">
                        {isPending && (
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleSelect(log._id)}
                            onClick={(e) => e.stopPropagation()}
                            className="rounded border-gray-500 bg-gray-700"
                          />
                        )}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-orange-400">
                        {log.logNumber || "—"}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-gray-400">
                        {log.sourceRequestId?.requestNumber || "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        <StatusBadge status={log.status} />
                      </td>
                      <td className="px-3 py-2.5">
                        <div>
                          <p className="text-gray-200 font-medium truncate max-w-[160px]">
                            {log.workOrder?.title || "—"}
                          </p>
                          <p className="text-[10px] text-gray-500 font-mono">
                            {log.workOrder?.woNumber}
                          </p>
                          {/* Work Order cell: add under the woNumber <p> */}
                          {log.moduleName && (
                            <p className="text-[10px] text-purple-400 truncate max-w-[160px]">
                              {log.moduleName}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-gray-300">
                        {log.worker?.displayName || log.worker?.email || "—"}
                      </td>
                      <td className="px-3 py-2.5 text-right text-gray-400 font-mono">
                        {(log.lineItems || []).length}
                      </td>
                      <td className="px-3 py-2.5 text-gray-400">
                        {fmtDateOnly(log.workCompletedDate)}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500">
                        {log.submittedAt
                          ? new Date(log.submittedAt).toLocaleDateString(
                              "en-CA",
                            )
                          : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-gray-400">
                        {log.status === "approved" ? (
                          <div>
                            <p className="text-gray-300 text-[11px]">
                              {log.approvedBy?.displayName || "—"}
                            </p>
                            {fmtDateOnly(log.approvedAt)}
                          </div>
                        ) : (
                          <span className="text-gray-700">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 min-w-[160px] max-w-[240px]">
                        {log.notes ? (
                          <p
                            className="text-gray-300 text-[11px] leading-snug whitespace-normal"
                            title={log.notes}
                          >
                            {log.notes}
                          </p>
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                        <TagChips
                          units={log.units}
                          tags={log.tags}
                          activeTags={tagFilter}
                          onTagClick={(t) =>
                            setTagFilter((p) =>
                              p.includes(t)
                                ? p.filter((x) => x !== t)
                                : [...p, t],
                            )
                          }
                          onUnitClick={(u) => setSearch(`unit ${u}`)}
                          className="mt-1"
                        />
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono">
                        <span className="text-gray-300">${fmt(sub)}</span>
                        {hasBurden && (
                          <span className="block text-[9px] text-gray-600 font-normal">
                            +${fmt((log.totalCost || 0) - sub)} burden
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono font-semibold text-orange-300">
                        ${fmt(log.totalCost || 0)}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() =>
                              navigate(`/piecework-logs3/${log._id}`, {
                                state: {
                                  fromProject: true,
                                  projectId: logProjectId,
                                  activeStatusTab:
                                    log.status.charAt(0).toUpperCase() +
                                    log.status.slice(1),
                                },
                              })
                            }
                            className="px-3 py-1 text-xs text-gray-300 bg-gray-700 rounded hover:bg-gray-600 transition-colors"
                          >
                            View
                          </button>
                          {/* Per-row PDF button */}
                          <button
                            onClick={() => {
                              const doc = buildPDF([log], project);
                              const pName =
                                project?.projectName ||
                                project?.projectNumber ||
                                "Project";
                              doc.save(
                                `${pName}_${log.logNumber || "worksheet"}.pdf`,
                              );
                            }}
                            className="px-2 py-1 text-xs text-red-400 bg-gray-700 rounded hover:bg-gray-600 transition-colors"
                            title="Export this worksheet as PDF"
                          >
                            PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              <tfoot className="bg-gray-900 border-t border-gray-600">
                <tr>
                  <td
                    colSpan={11}
                    className="px-3 py-2.5 text-xs text-gray-400"
                  >
                    {filtered.length} total
                    {filtered.length > paginated.length &&
                      ` · showing ${paginated.length} on this page`}
                  </td>
                  <td className="px-3 py-2.5 text-right text-sm font-bold text-gray-200 font-mono">
                    ${fmt(filtered.reduce((s, l) => s + logSubtotal(l), 0))}
                  </td>
                  <td className="px-3 py-2.5 text-right text-sm font-bold text-orange-300 font-mono">
                    ${fmt(filtered.reduce((s, l) => s + (l.totalCost || 0), 0))}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className={cls(
              "px-4 py-2 text-sm rounded-lg text-white transition-colors",
              page === 1
                ? "bg-gray-700 cursor-not-allowed text-gray-500"
                : "bg-gray-700 hover:bg-gray-600",
            )}
          >
            ← Previous
          </button>
          <span className="text-sm text-gray-400">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className={cls(
              "px-4 py-2 text-sm rounded-lg text-white transition-colors",
              page === totalPages
                ? "bg-gray-700 cursor-not-allowed text-gray-500"
                : "bg-gray-700 hover:bg-gray-600",
            )}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
